import { useState } from 'react'
import { t } from '@/i18n'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ackRiskEvent,
  dismissRiskEvent,
  evaluateRisk,
  getAlerts,
  getRiskEvents,
  getRiskRules,
  markAlertRead,
  patchRiskRule,
  snoozeAlert,
  type AlertOut,
  type RiskEventOut,
  type RiskFactor,
} from '@/lib/api'
import { formatDateTime } from '@/lib/format'

const SEVERITY_CLASS: Record<string, string> = {
  critical: 'text-loss',
  high: 'text-pending',
  medium: 'text-slate-200',
  low: 'text-slate-400',
  info: 'text-slate-500',
}

export function Alerts() {
  const queryClient = useQueryClient()
  const [openId, setOpenId] = useState<string | null>(null)
  const alerts = useQuery({ queryKey: ['alerts'], queryFn: getAlerts })
  const events = useQuery({ queryKey: ['risk', 'events'], queryFn: getRiskEvents })
  const rules = useQuery({ queryKey: ['risk', 'rules'], queryFn: getRiskRules })

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['alerts'] })
    void queryClient.invalidateQueries({ queryKey: ['risk'] })
  }

  const evaluate = useMutation({
    mutationFn: () => evaluateRisk('manual'),
    onSuccess: invalidate,
  })
  const read = useMutation({
    mutationFn: markAlertRead,
    onSuccess: invalidate,
  })
  const snooze = useMutation({
    mutationFn: (id: string) => snoozeAlert(id, 60),
    onSuccess: invalidate,
  })
  const ack = useMutation({
    mutationFn: (id: string) => ackRiskEvent(id),
    onSuccess: invalidate,
  })
  const dismiss = useMutation({
    mutationFn: (id: string) => dismissRiskEvent(id),
    onSuccess: invalidate,
  })
  const toggleRule = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) => patchRiskRule(id, { enabled }),
    onSuccess: invalidate,
  })

  const unread = (alerts.data ?? []).filter((row) => !row.read_at && row.status !== 'read')

  return (
    <div className="mx-auto max-w-6xl px-4 py-5 md:px-8 md:py-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-100">{t('al.title')}</h1>
          <p className="mt-1 text-sm text-slate-500">{t('al.lead')}</p>
        </div>
        <button
          type="button"
          className="rounded-md bg-slate-100 px-3 py-2 text-sm text-slate-900 disabled:opacity-50"
          disabled={evaluate.isPending}
          onClick={() => evaluate.mutate()}
        >
          {evaluate.isPending ? t('al.evaling') : t('al.eval')}
        </button>
      </div>
      {evaluate.isSuccess && (
        <p className="mt-3 text-sm text-profit">
          {t('al.done', { e: evaluate.data.events, a: evaluate.data.alerts, u: evaluate.data.unmatched_released })}
        </p>
      )}

      <section className="mt-8">
        <h2 className="text-sm font-medium text-slate-200">
          {t('al.feed')} {unread.length > 0 ? t('al.unread', { n: unread.length }) : ''}
        </h2>
        {alerts.isLoading && <p className="mt-3 text-sm text-slate-500">{t('common.loading')}</p>}
        {alerts.data && alerts.data.length === 0 && (
          <p className="mt-3 text-sm text-slate-500">{t('al.empty')}</p>
        )}
        <div className="mt-3 space-y-2">
          {(alerts.data ?? []).map((row) => (
            <AlertCard
              key={row.id}
              row={row}
              open={openId === row.id}
              onToggle={() => setOpenId(openId === row.id ? null : row.id)}
              onRead={() => read.mutate(row.id)}
              onSnooze={() => snooze.mutate(row.id)}
            />
          ))}
        </div>
      </section>

      <section className="mt-10">
        <h2 className="text-sm font-medium text-slate-200">{t('al.open')}</h2>
        <div className="mt-3 space-y-2">
          {(events.data ?? [])
            .filter((row) => row.status === 'open' || row.status === 'acknowledged')
            .map((row) => (
              <EventCard
                key={row.id}
                row={row}
                onAck={() => ack.mutate(row.id)}
                onDismiss={() => dismiss.mutate(row.id)}
              />
            ))}
        </div>
      </section>

      <section className="mt-10">
        <h2 className="text-sm font-medium text-slate-200">{t('al.rules')}</h2>
        <p className="mt-1 text-xs text-slate-500">{t('al.rulesLead')}</p>
        <table className="mt-3 w-full text-sm">
          <thead className="text-left text-xs text-slate-500">
            <tr>
              <th className="py-1">{t('al.rule')}</th>
              <th className="py-1">{t('al.weight')}</th>
              <th className="py-1">{t('al.threshold')}</th>
              <th className="py-1">{t('al.enabled')}</th>
            </tr>
          </thead>
          <tbody>
            {(rules.data ?? []).map((rule) => (
              <tr key={rule.id} className="border-t border-surface-border/60">
                <td className="py-2">
                  <div className={SEVERITY_CLASS[rule.severity] ?? 'text-slate-200'}>{rule.name}</div>
                  <div className="text-xs text-slate-500">{rule.description}</div>
                </td>
                <td className="num py-2">{rule.weight}</td>
                <td className="num py-2 text-slate-400">{formatParams(rule.params)}</td>
                <td className="py-2">
                  <button
                    type="button"
                    className="text-sm text-slate-300 hover:text-white"
                    onClick={() => toggleRule.mutate({ id: rule.id, enabled: !rule.enabled })}
                  >
                    {rule.enabled ? t('common.yes') : t('common.no')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  )
}

function AlertCard({
  row,
  open,
  onToggle,
  onRead,
  onSnooze,
}: {
  row: AlertOut
  open: boolean
  onToggle: () => void
  onRead: () => void
  onSnooze: () => void
}) {
  const factors = (row.payload?.factors as RiskFactor[] | undefined) ?? []
  return (
    <article className="rounded-lg border border-surface-border bg-surface-raised px-4 py-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <button type="button" className="text-left" onClick={onToggle}>
          <div className={`text-sm ${SEVERITY_CLASS[row.severity] ?? 'text-slate-200'}`}>{row.title}</div>
          <div className="mt-0.5 text-xs text-slate-500">
            {formatDateTime(row.created_at)} · {row.status} · {row.delivered_channels.join(', ') || 'app'}
          </div>
        </button>
        <div className="flex gap-2 text-xs">
          {!row.read_at && (
            <button type="button" className="text-slate-300 hover:text-white" onClick={onRead}>
              {t('al.read')}
            </button>
          )}
          {row.status !== 'read' && (
            <button type="button" className="text-slate-400 hover:text-white" onClick={onSnooze}>
              {t('al.snooze')}
            </button>
          )}
        </div>
      </div>
      {open && (
        <div className="mt-3 whitespace-pre-wrap text-sm text-slate-400">
          {row.body}
          {factors.length > 0 && (
            <ul className="mt-2 space-y-1">
              {factors.map((item) => (
                <li key={item.code}>
                  <span className="text-slate-200">{item.label}</span>
                  <span className="num text-slate-500">{t('cpp.weight', { n: item.weight })}</span>
                  {item.detail ? ` — ${item.detail}` : ''}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </article>
  )
}

function EventCard({
  row,
  onAck,
  onDismiss,
}: {
  row: RiskEventOut
  onAck: () => void
  onDismiss: () => void
}) {
  return (
    <article className="rounded-md border border-surface-border px-4 py-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className={`text-sm ${SEVERITY_CLASS[row.severity] ?? 'text-slate-200'}`}>{row.title}</div>
          <div className="mt-0.5 text-xs text-slate-500">
            {t('al.score', { n: row.score ?? '—' })} · {formatDateTime(row.detected_at)}
            {row.counterparty_id ? (
              <>
                {' · '}
                <Link to={`/counterparties/${row.counterparty_id}`} className="text-slate-300 hover:text-white">
                  {t('al.profile')}
                </Link>
              </>
            ) : null}
          </div>
          <ul className="mt-2 space-y-0.5 text-xs text-slate-400">
            {row.factors.map((item) => (
              <li key={item.code}>
                {item.label} <span className="num">({item.weight})</span>
                {item.detail ? ` — ${item.detail}` : ''}
              </li>
            ))}
          </ul>
        </div>
        <div className="flex gap-2 text-xs">
          <button type="button" className="text-slate-300 hover:text-white" onClick={onAck}>
            {t('al.ack')}
          </button>
          <button type="button" className="text-slate-400 hover:text-white" onClick={onDismiss}>
            {t('al.dismiss')}
          </button>
        </div>
      </div>
    </article>
  )
}

function formatParams(params: Record<string, unknown>): string {
  const entries = Object.entries(params)
  if (entries.length === 0) return '—'
  return entries.map(([key, value]) => `${key}=${String(value)}`).join(', ')
}
